// Get user's timezone from database - NEW: No fallback to browser
export const getUserTimezone = async (teamId, userId) => {
  try {
    console.log('🟨 getUserTimezone: Getting timezone from database', {
      teamId,
      userId,
      timestamp: new Date().toISOString(),
    });

    // Import the team service to get timezone from database
    const teamDatabase = (await import('../services/team')).default;

    const response = await teamDatabase.getUserTimezoneFromDB(teamId, userId);

    console.log('🟨 getUserTimezone: Database response', {
      success: response.success,
      timezone: response.timezone,
      error: response.error,
    });

    if (response.success) {
      return {
        timezone: response.timezone,
        formatted: `${response.timezone}`,
        source: response.source || 'database',
      };
    } else {
      // Fallback to IST if database lookup fails
      console.log(
        '🟨 getUserTimezone: Database lookup failed, using IST fallback',
        {
          error: response.error,
          fallbackTimezone: 'Asia/Kolkata',
        }
      );
      return {
        timezone: 'Asia/Kolkata',
        formatted: 'Asia/Kolkata',
        source: 'fallback',
      };
    }
  } catch (error) {
    console.error(
      '🟨 getUserTimezone: Error getting timezone from database, using IST fallback:',
      {
        error: error.message,
        teamId,
        userId,
        fallbackTimezone: 'Asia/Kolkata',
      }
    );

    // Fallback to IST instead of throwing error
    return {
      timezone: 'Asia/Kolkata',
      formatted: 'Asia/Kolkata',
      source: 'fallback',
    };
  }
};

// Legacy browser-based timezone getter (deprecated) - kept for reference only
export const getBrowserTimezone = () => {
  try {
    // Get timezone name (e.g., "America/New_York")
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

    // Get timezone offset in minutes
    const offset = new Date().getTimezoneOffset();

    // Convert offset to hours and minutes
    const offsetHours = Math.abs(Math.floor(offset / 60));
    const offsetMinutes = Math.abs(offset % 60);
    const offsetSign = offset <= 0 ? '+' : '-';

    // Format offset string (e.g., "+05:30" for IST)
    const offsetString = `${offsetSign}${offsetHours.toString().padStart(2, '0')}:${offsetMinutes.toString().padStart(2, '0')}`;

    return {
      timezone, // e.g., "America/New_York"
      offset, // e.g., -330 (for IST)
      offsetString, // e.g., "+05:30"
      formatted: `${timezone} (UTC${offsetString})`, // e.g., "America/New_York (UTC+05:30)"
      source: 'browser',
    };
  } catch (error) {
    console.error('Error getting browser timezone:', error);
    return {
      timezone: 'UTC',
      offset: 0,
      offsetString: '+00:00',
      formatted: 'UTC (UTC+00:00)',
      source: 'fallback',
    };
  }
};

// Format date in user's timezone - fallback to browser timezone for notifications
export const formatDateInTimezone = async (
  date,
  options = {},
  teamId = null,
  userId = null
) => {
  try {
    let timezone;

    if (teamId && userId) {
      // Use database timezone when team/user info is available
      const userTimezone = await getUserTimezone(teamId, userId);
      timezone = userTimezone.timezone;
    } else {
      // Fallback to browser timezone for notifications and other contexts
      const browserTimezone = getBrowserTimezone();
      timezone = browserTimezone.timezone;
    }

    const defaultOptions = {
      timeZone: timezone,
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: true,
    };

    return new Intl.DateTimeFormat('en-US', {
      ...defaultOptions,
      ...options,
    }).format(date);
  } catch (error) {
    console.error('Error formatting date:', error);
    return date.toString();
  }
};

// Get current time in user's timezone - fallback to browser timezone for notifications
export const getCurrentTimeInTimezone = (teamId = null, userId = null) => {
  if (teamId && userId) {
    // Return promise when team/user info is available
    return formatDateInTimezone(new Date(), {}, teamId, userId);
  } else {
    // Synchronous fallback to browser timezone for notifications
    try {
      const browserTimezone = getBrowserTimezone();
      const defaultOptions = {
        timeZone: browserTimezone.timezone,
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: true,
      };
      return new Intl.DateTimeFormat('en-US', defaultOptions).format(
        new Date()
      );
    } catch (error) {
      console.error('Error getting current time in timezone:', error);
      return new Date().toLocaleString();
    }
  }
};

// Example usage:
// const timezoneInfo = getUserTimezone();
// console.log(timezoneInfo.formatted);  // e.g., "America/New_York (UTC-05:00)"
//
// const currentTime = getCurrentTimeInTimezone();
// console.log(currentTime);  // e.g., "January 1, 2024, 12:00:00 PM"
